import { EmailAPI } from '@tacans/shared-marketplace-layer';
// eslint-disable-next-line no-restricted-imports
import Image from 'next/image';
import React, { useCallback, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import 'react-phone-input-2/lib/bootstrap.css';
import {
    StyledContainer,
    StyledFormWrapper,
    StyledHighlightedText,
    StyledWrapper,
} from 'src/features/white-labels/techcontemporary/app/components/sign-up-component/sign-up-component.styles';
import { StyledInputWrapper } from 'src/features/white-labels/techcontemporary/checkout/components/checkout-layout/checkout-layout.styles';
import {
    StyledColumns,
    StyledPhoneInput,
} from 'src/features/white-labels/techcontemporary/checkout/containers/shipping-billing-form/shipping-billing-form.styles';
import { StyledHelpMessage } from 'src/features/white-labels/techcontemporary/profile/containers/profile-edit-containers/profile-edit-containers.styles';
import { useNotifications } from 'src/services/notifications/use-notifications';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Button } from 'src/ui/white-labels/techcontemporary/atoms/button';
import { Checkbox } from 'src/ui/white-labels/techcontemporary/atoms/checkbox/checkbox';
import { FormTextInput } from 'src/ui/white-labels/techcontemporary/molecules/form-text-input';
import { useValidation } from 'src/utils/validation.utils';
import { StyledTypography } from '../auth/auth.styles';

export const SignUpComponent = () => {
    const { success, error } = useNotifications();
    const successNotification = useCallback(
        (notification) => {
            success({
                text: notification,
            });
        },
        [success]
    );

    const errorNotification = useCallback(
        (notification) => {
            error({
                text: notification,
            });
        },
        [error]
    );
    const { t } = useTranslation(['tech-contemporary']);
    const locale = useMemo(
        () => ({
            fullNamePlaceholder: t('signUpComponent.fullNamePlaceholder', { ns: 'tech-contemporary' }),
            emailPlaceholder: t('signUpComponent.emailPlaceholder', { ns: 'tech-contemporary' }),
        }),
        [t]
    );
    const methods = useForm({});
    const {
        handleSubmit,
        register,
        getValues,
        setValue,
        clearErrors,
        control,
        formState: { errors },
    } = methods;
    const values = getValues();
    const { isRequired, validateEmail, validateTechPhone, validatePattern } = useValidation();
    const onFormFieldChange = useCallback(
        ({ target }: React.ChangeEvent<HTMLInputElement>) => {
            setValue(target.name, target.value);
            clearErrors();
        },
        [values]
    );
    const [isAgree, setAgree] = useState(false);

    const agreeToggle = (e: any) => {
        setAgree(e.target.checked);
    };

    const onSubmit = useCallback(async (data) => {
        const { fullName, email, phoneNumber } = data;
        try {
            const res = await EmailAPI.emailAdd({ fullName, email, phone: phoneNumber });
            if (res?.data.responseMeta.status === 'OK') {
                successNotification('Thank you for signing up!');
                setValue('email', '');
                setValue('fullName', '');
                setValue('phoneNumber', '');
                setAgree(false);
            } else {
                errorNotification(res?.data.responseMeta.error?.messages);
            }
        } catch (e) {
            console.log(e);
        }
    }, []);

    return (
        <StyledWrapper>
            <StyledContainer>
                <Spacing mb="48px">
                    <FlexBox gap="16" flexDirection="column" alignItems="center">
                        <Image src="/whitelabel/tech-contemporary/news&events.svg" width={538} height={48} />
                        <StyledHighlightedText variant="title" color="white">
                            delivered right to your inbox
                        </StyledHighlightedText>
                    </FlexBox>
                </Spacing>

                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <StyledFormWrapper>
                            <StyledColumns withGap flexDirection="column">
                                <FormTextInput
                                    type="fullName"
                                    {...register('fullName', {
                                        ...isRequired(),
                                        ...validatePattern(
                                            /^[A-Za-z\s\-']+$/,
                                            'String can consisting of letters (both uppercase and lowercase), spaces, hyphens, and single quotes'
                                        ),
                                        onChange: onFormFieldChange,
                                        value: values.fullName,
                                    })}
                                    placeholder={locale.fullNamePlaceholder}
                                    error={errors.fullName?.message}
                                />
                            </StyledColumns>
                            <StyledColumns withGap flexDirection="column">
                                <FormTextInput
                                    type="email"
                                    {...register('email', {
                                        ...isRequired(),
                                        ...validateEmail(),
                                        onChange: onFormFieldChange,
                                        value: values.email,
                                    })}
                                    placeholder={locale.emailPlaceholder}
                                    error={errors.email?.message}
                                />
                            </StyledColumns>
                            <StyledInputWrapper flexDirection="column">
                                <Controller
                                    control={control}
                                    name="phoneNumber"
                                    rules={{ required: true }}
                                    render={({ field: { ref } }) => (
                                        <StyledPhoneInput
                                            country="dk"
                                            {...register('phoneNumber', {
                                                ...validateTechPhone(),
                                                ...isRequired(),
                                            })}
                                            excludeCountries={['ru', 'rus']}
                                            onChange={(phone) => {
                                                setValue('phoneNumber', phone, { shouldDirty: true });
                                                clearErrors('phoneNumber');
                                            }}
                                            value={values.phoneNumber}
                                            inputClass="input-styles"
                                            buttonClass="dropdown-styles"
                                        />
                                    )}
                                />
                                {errors.phoneNumber?.message && (
                                    <StyledHelpMessage state="error">{errors.phoneNumber?.message}</StyledHelpMessage>
                                )}
                            </StyledInputWrapper>

                            <StyledInputWrapper alignItems="center" gap="5">
                                <Checkbox name="agree" onChange={agreeToggle} checked={isAgree} />
                                <StyledTypography tag="span" variant="text" size="xs" color="grey300">
                                    I agree to receive newsletter emails from Tech Contemporary{' '}
                                </StyledTypography>
                            </StyledInputWrapper>
                            <Button stretch type="submit" disabled={!isAgree} size="lg">
                                Sign Up
                            </Button>
                        </StyledFormWrapper>
                    </form>
                </FormProvider>
            </StyledContainer>
        </StyledWrapper>
    );
};